// src/lib/public-route.js — Fiches d'arrivée LOT 3 (ADR-188)
//
// Résolution PURE du routing public par pathname (pas de React Router).
// Extraite de src/index.js pour être unit-testable (la regex du routing
// vivait inline dans le module d'entrée non-importable en test).
//
// Alias /bienvenue/ (LOT 3) : route STRICTEMENT identique à /forms/ — les deux
// montent PublicFormPage avec le même token. Les liens /forms/ historiques
// restent routés À VIE (zéro duplication de composant, juste 2 entrées de match).

// Retourne { kind, token? , slug?, moduleId? } ou null si aucune route publique.
export function matchPublicRoute(path) {
  if (typeof path !== 'string' || !path) return null

  // LOT 3 (ADR-188) — /bienvenue/<token> ET /forms/<token> montent PublicFormPage.
  // Alias strict : même composant, même token, même garde UUID 36 chars.
  const mForm = path.match(/^\/(?:forms|bienvenue)\/([0-9a-f-]{36})\/?$/i)
  if (mForm) return { kind: 'form', token: mForm[1] }

  // Route module detail : /livret/r/:token/module/:moduleId
  // (DOIT précéder le pattern /livret/r/:token).
  const mLivretModule = path.match(
    /^\/livret\/r\/([0-9a-f-]{36})\/module\/([a-zA-Z0-9_-]{1,64})\/?$/i,
  )
  if (mLivretModule)
    return {
      kind: 'livret_module',
      token: mLivretModule[1],
      moduleId: mLivretModule[2],
    }

  const mLivret = path.match(/^\/livret\/r\/([0-9a-f-]{36})\/?$/i)
  if (mLivret) return { kind: 'livret_token', token: mLivret[1] }

  const mQR = path.match(/^\/livret\/qr\/([a-z0-9-]{2,80})\/?$/)
  if (mQR) return { kind: 'livret_qr', slug: mQR[1] }

  // UUID strict 36 chars, sinon page erreur explicite (pas page blanche).
  const mPaiement = path.match(/^\/paiement\/([0-9a-f-]{36})\/?$/i)
  if (mPaiement) return { kind: 'paiement', token: mPaiement[1] }
  if (/^\/paiement\//i.test(path)) return { kind: 'paiement', token: null }

  // Lot O1 OTA — /sejour-ota/:token → JourneyOtaPage (espace voyageur OTA).
  // Placé AVANT /sejour/ par lisibilité (les deux préfixes sont disjoints :
  // le catch-all /^\/sejour\// exige le « / » après « sejour », il ne peut
  // pas absorber /sejour-ota/…). Double-pattern identique à /sejour.
  const mJourneyOta = path.match(/^\/sejour-ota\/([0-9a-f-]{36})\/?$/i)
  if (mJourneyOta) return { kind: 'journey_ota', token: mJourneyOta[1] }
  if (/^\/sejour-ota\//i.test(path)) return { kind: 'journey_ota', token: null }

  // /sejour/:token → JourneyPage (double-pattern UUID strict + catch-all malformé).
  const mJourney = path.match(/^\/sejour\/([0-9a-f-]{36})\/?$/i)
  if (mJourney) return { kind: 'journey', token: mJourney[1] }
  if (/^\/sejour\//i.test(path)) return { kind: 'journey', token: null }

  // Option B M1 (PR #962) — /s/:code (lien court SMS, 12 hex stricts) →
  // ShortJourneyRedirectPage : résout code → journey_token (action s_resolve)
  // puis redirige vers /sejour/<token>. Double-pattern comme /sejour (code
  // malformé → code null → page « Lien invalide ou expiré » sans appel réseau).
  const mShort = path.match(/^\/s\/([0-9a-f]{12})\/?$/i)
  if (mShort) return { kind: 'short_journey', code: mShort[1].toLowerCase() }
  if (/^\/s\//i.test(path)) return { kind: 'short_journey', code: null }

  // CHANTIER LIEN COURT — /l/:code (hub unifié guest_links) → GuestLinkRedirectPage.
  // Code = 12 caractères Crockford base32 (60 bits ; alphabet 0-9 A-Z sans
  // I,L,O,U). Branche DISJOINTE de /s/ (legacy 12-hex, intact). La page résout
  // code → { kind, target_token } via l'action gl_resolve puis redirige vers la
  // route interne longue du kind. Double-pattern comme /s/ : code malformé →
  // code null → page « Lien invalide ou expiré » sans appel réseau. Le code est
  // normalisé en MAJUSCULES (Crockford insensible à la casse en saisie ;
  // stockage canonique majuscule). Doit rester aligné avec GUEST_CODE_REGEX
  // (lib/guest-links.js) et le CHECK de la migration guest_links.
  const mGuestLink = path.match(/^\/l\/([0-9A-HJKMNP-TV-Z]{12})\/?$/i)
  if (mGuestLink) return { kind: 'guest_link', code: mGuestLink[1].toUpperCase() }
  if (/^\/l\//i.test(path)) return { kind: 'guest_link', code: null }

  // WAGON D LOT D-A (ADR-191) — /recu/:token → RecuPage (document figé du séjour).
  // UUID strict 36 chars, sinon page erreur explicite (token null → "invalide").
  const mRecu = path.match(/^\/recu\/([0-9a-f-]{36})\/?$/i)
  if (mRecu) return { kind: 'recu', token: mRecu[1] }
  if (/^\/recu\//i.test(path)) return { kind: 'recu', token: null }

  // CHANTIER OTA Lot O3 — /unsubscribe → UnsubscribePage (désinscription
  // marketing 1 clic). Le jeton vit en QUERY (?token=<uuid>) : lu par le
  // composant (le matcher reste pathname pur, comme services-payment/done).
  if (/^\/unsubscribe\/?$/i.test(path)) return { kind: 'unsubscribe' }

  // Stripe Checkout return_url (query parsing fait dans le composant).
  if (/^\/services-payment\/done\/?$/i.test(path))
    return { kind: 'services_payment_return' }

  return null
}
